import React, { useId, useMemo, useState } from 'react'
import dayjs from 'dayjs'

// types
import { FilterValuesType, Cosmetic, GetSalonsFilterCountResponse, Language, LoadSalonsFromFiltersParams, FilterQuery, Currency } from '../../types/types'

// components
import SalonsChips from '../SalonsChips/SalonsChips'
import FilterSidebar from '../FilterSidebar/FilterSidebar'

// utils
import { SALONS_FILTER_ITEMS_CONFIG } from '../../utils/helper'
import { OPENING_HOURS_STATUS, SALONS_FILTER_TYPE } from '../../utils/enums'

// hooks
import useSortingOptions from '../../hooks/useSortingOptions'

// types
type FiltersProps = {
	query: FilterQuery
	filterCounts: GetSalonsFilterCountResponse | undefined
	loadSalonsFromFilters: (params: LoadSalonsFromFiltersParams) => void
	languages: Language[]
	cosmetics: Cosmetic[]
	currency: Currency
	currencySecondary: Currency | undefined
	initialClientDataLoading?: boolean
	topLevelCategoryID: string | undefined
	maxAvResTimeSlotDateRange: number
	lat: number | undefined
	lon: number | undefined
}

// component
const Filters = (props: FiltersProps) => {
	const {
		languages,
		cosmetics,
		currency,
		currencySecondary,
		loadSalonsFromFilters,
		query,
		filterCounts,
		initialClientDataLoading,
		topLevelCategoryID,
		maxAvResTimeSlotDateRange,
		lat,
		lon
	} = props

	const [, sortingOptions] = useSortingOptions(query)

	const filtersDrawerId = useId()
	const filterDetailDrawerId = useId()

	const [filterSidebarOpen, setFilterSidebarOpen] = useState(false)
	const [filterDetailOpenedDirectly, setFilterDetailOpenedDirectly] = useState(false)
	const [filterSidebarDetailOpen, setFilterSidebarDetailOpen] = useState(false)
	const [filterSidebarDetailType, setFilterSidebarDetailType] = useState<SALONS_FILTER_TYPE | null>(null)

	const filterItemsConfig = useMemo(
		() => SALONS_FILTER_ITEMS_CONFIG(filterCounts?.priceRange.minValue, filterCounts?.priceRange?.maxValue),
		[filterCounts?.priceRange.minValue, filterCounts?.priceRange?.maxValue]
	)

	const defaultFilterValues: FilterValuesType = useMemo(
		() => ({
			serviceTotalPriceRange: {
				priceFrom: filterItemsConfig[SALONS_FILTER_TYPE.PRICE].defaultValue[0],
				priceTo: filterItemsConfig[SALONS_FILTER_TYPE.PRICE].defaultValue[1]
			},
			hasAvailableReservationSystem: filterItemsConfig[SALONS_FILTER_TYPE.AVAILABLE_RS].defaultValue,
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// hasGift: filterItemsConfig[SALONS_FILTER_TYPE.HAS_GIFT].defaultValue,
			openingHours: filterItemsConfig[SALONS_FILTER_TYPE.OPENING_HOURS].defaultValue,
			exactRating: filterItemsConfig[SALONS_FILTER_TYPE.RATING].defaultValue,
			cosmeticIDs: filterItemsConfig[SALONS_FILTER_TYPE.COSMETICS].defaultValue,
			languageIDs: filterItemsConfig[SALONS_FILTER_TYPE.LANGUAGES].defaultValue,
			orderBy: filterItemsConfig[SALONS_FILTER_TYPE.SORTING].defaultValue,
			availableReservations: {
				timeSlot: filterItemsConfig[SALONS_FILTER_TYPE.DATE].defaultValue.avResTimeSlotDayPart,
				dateFrom: filterItemsConfig[SALONS_FILTER_TYPE.DATE].defaultValue.avResTimeSlotDateFrom,
				dateTo: filterItemsConfig[SALONS_FILTER_TYPE.DATE].defaultValue.avResTimeSlotDateTo
			}
		}),
		[filterItemsConfig]
	)

	// needs to be memoized to prevent unexpected reset of internal filter values
	const submittedFilterValues: FilterValuesType = useMemo(() => {
		const dateFrom = query.avResTimeSlotDateFrom || query.avResTimeSlotDate || defaultFilterValues.availableReservations.dateFrom

		return {
			serviceTotalPriceRange: {
				priceFrom: query.serviceTotalPriceFrom ?? defaultFilterValues.serviceTotalPriceRange.priceFrom,
				priceTo: query.serviceTotalPriceTo ?? defaultFilterValues.serviceTotalPriceRange.priceTo
			},
			hasAvailableReservationSystem: query.hasAvailableReservationSystem ?? defaultFilterValues.hasAvailableReservationSystem,
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// hasGift: query.hasGift ?? defaultFilterValues.hasGift,
			openingHours: query.openingHoursStatus === OPENING_HOURS_STATUS.OPEN || defaultFilterValues.openingHours,
			exactRating: query.exactRating || defaultFilterValues.exactRating,
			cosmeticIDs: query.cosmeticIDs || defaultFilterValues.cosmeticIDs,
			languageIDs: query.languageIDs || defaultFilterValues.languageIDs,
			orderBy: query.orderBy || defaultFilterValues.orderBy,
			availableReservations: {
				timeSlot: query.avResTimeSlotDayPart || defaultFilterValues.availableReservations.timeSlot,
				dateFrom: dateFrom ? dayjs(dateFrom) : undefined,
				dateTo:
					query.avResTimeSlotDateTo || defaultFilterValues.availableReservations.dateTo
						? dayjs(query.avResTimeSlotDateTo || defaultFilterValues.availableReservations.dateTo)
						: undefined
			}
		}
	}, [
		query.serviceTotalPriceFrom,
		query.serviceTotalPriceTo,
		query.hasAvailableReservationSystem,
		// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
		// query.hasGift,
		query.openingHoursStatus,
		query.exactRating,
		query.cosmeticIDs,
		query.languageIDs,
		query.orderBy,
		query.avResTimeSlotDayPart,
		query.avResTimeSlotDate,
		query.avResTimeSlotDateFrom,
		query.avResTimeSlotDateTo,
		defaultFilterValues
	])

	return (
		<>
			<SalonsChips
				filterItemsConfig={filterItemsConfig}
				submittedFilterValues={submittedFilterValues}
				defaultFilterValues={defaultFilterValues}
				setFilterSidebarOpen={setFilterSidebarOpen}
				setFilterSidebarDetailOpen={setFilterSidebarDetailOpen}
				setFilterSidebarDetailType={setFilterSidebarDetailType}
				setFilterDetailOpenedDirectly={setFilterDetailOpenedDirectly}
				filterSidebarOpen={filterSidebarOpen}
				filterSidebarDetailOpen={filterSidebarDetailOpen}
				filterSidebarDetailType={filterSidebarDetailType}
				filtersDrawerId={filtersDrawerId}
				filterDetailDrawerId={filterDetailDrawerId}
				cosmetics={cosmetics}
				languages={languages}
				loadSalonsFromFilters={loadSalonsFromFilters}
				sortingOptions={sortingOptions}
				initialClientDataLoading={initialClientDataLoading}
				filterCounts={filterCounts}
				currencyCode={currency.code}
				currencyCodeSecondary={currencySecondary?.code}
			/>
			<FilterSidebar
				submittedFilterValues={submittedFilterValues}
				defaultFilterValues={defaultFilterValues}
				filterItemsConfig={filterItemsConfig}
				filterSidebarOpen={filterSidebarOpen}
				setFilterSidebarOpen={setFilterSidebarOpen}
				filterSidebarDetailOpen={filterSidebarDetailOpen}
				setFilterSidebarDetailOpen={setFilterSidebarDetailOpen}
				filterSidebarDetailType={filterSidebarDetailType}
				setFilterSidebarDetailType={setFilterSidebarDetailType}
				filterDetailOpenedDirectly={filterDetailOpenedDirectly}
				setFilterDetailOpenedDirectly={setFilterDetailOpenedDirectly}
				filtersDrawerId={filtersDrawerId}
				filterDetailDrawerId={filterDetailDrawerId}
				sortingOptions={sortingOptions}
				initialCosmetics={cosmetics}
				initialLanguages={languages}
				currency={currency}
				currencySecondary={currencySecondary}
				loadSalonsFromFilters={loadSalonsFromFilters}
				filterCounts={filterCounts}
				query={query}
				initialClientDataLoading={initialClientDataLoading}
				topLevelCategoryID={topLevelCategoryID}
				maxAvResTimeSlotDateRange={maxAvResTimeSlotDateRange}
				lat={lat}
				lon={lon}
			/>
		</>
	)
}

export default Filters
